import { useSelector } from "react-redux";
import { getSearchResultsSlice } from "../store/searchResults/selectors.js";
import { getFlightDetailsSlice } from "../store/flightDetails/selectors.js";
import { getMetaFlightDetailsSlice } from "../store/metaFlightDetails/selectors.js";
import useClientMetrics from "./useClientMetrics.js";
import { useCallback, useEffect } from "react";
import { isMetaLandingOfferToken } from "../utils/marketing-url-params.js";
import { dataUtils, pricingUtils } from "@flights/web-api-utils-universal";
import { flightsSessionStore } from "@flights/web-api-utils-universal";
import { PRICE_DISCREPANCY_LOGGED_OFFER } from "../constants.js";
const useReportPriceDiscrepancy = (flightToken) => {
    const searchResults = useSelector(getSearchResultsSlice);
    const flightDetails = useSelector(getFlightDetailsSlice);
    const metaFlightDetails = useSelector(getMetaFlightDetailsSlice);
    const { trackClientMetric } = useClientMetrics();
    const ACCEPTED_PRICE_DIFFERENCE_THRESHOLD = 5;
    const logPriceDiscrepancy = useCallback((baseAPI, basePrice, targetPrice) => {
        if (pricingUtils.arePricesDifferent(basePrice.total, targetPrice.total)) {
            const percentageDifference = dataUtils.percentageDiff(pricingUtils.priceToNumber(basePrice.total), pricingUtils.priceToNumber(targetPrice.total));
            if (baseAPI == "FORAP") {
                trackClientMetric("price_mismatch_forap_gapd");
                trackClientMetric("price_mismatch_forap_gapd_difference", Math.abs(percentageDifference));
                if (percentageDifference > ACCEPTED_PRICE_DIFFERENCE_THRESHOLD) {
                    trackClientMetric("price_mismatch_forap_gapd_increase_above_5_percent");
                }
                if (percentageDifference < ACCEPTED_PRICE_DIFFERENCE_THRESHOLD * -1) {
                    trackClientMetric("price_mismatch_forap_gapd_decrease_below_5_percent");
                }
            }
        }
    }, [trackClientMetric]);
    const logPriceDiscrepancyTypes = useCallback((baseFlight, targetFlight, baseFlightType) => {
        if (!baseFlight || !targetFlight) {
            return;
        }
        const baseFlightTotalPrice = baseFlight.priceBreakdown;
        const targetFlightTotalPrice = targetFlight.priceBreakdown;
        const baseFlightCabinBaggagePrice = baseFlight.ancillaries?.cabinBaggagePerTraveller?.priceBreakdown;
        const baseFlightCheckedInBaggagePrice = baseFlight.ancillaries?.checkedInBaggage?.options[0]?.priceBreakdown;
        const targetFlightCabinBaggagePrice = targetFlight.ancillaries?.cabinBaggagePerTraveller?.priceBreakdown;
        const targetFlightCheckedInBaggagePrice = targetFlight.ancillaries?.checkedInBaggage?.options[0]?.priceBreakdown;
        baseFlightTotalPrice &&
            targetFlightTotalPrice &&
            logPriceDiscrepancy(baseFlightType, baseFlightTotalPrice, targetFlightTotalPrice);
        baseFlightCabinBaggagePrice &&
            targetFlightCabinBaggagePrice &&
            logPriceDiscrepancy(baseFlightType, baseFlightCabinBaggagePrice, targetFlightCabinBaggagePrice);
        baseFlightCheckedInBaggagePrice &&
            targetFlightCheckedInBaggagePrice &&
            logPriceDiscrepancy(baseFlightType, baseFlightCheckedInBaggagePrice, targetFlightCheckedInBaggagePrice);
    }, [logPriceDiscrepancy]);
    useEffect(() => {
        const savedOffers = flightsSessionStore.get(PRICE_DISCREPANCY_LOGGED_OFFER) || {};
        if (!savedOffers[flightToken] &&
            searchResults.fetchStatus === "success" &&
            flightDetails.fetchStatus === "success") {
            const flightFromFAP = searchResults.flights.find((flight) => flight.token == flightToken);
            const flightFromGAPD = flightDetails;
            if (flightFromFAP && flightFromGAPD?.flight) {
                logPriceDiscrepancyTypes(flightFromFAP, flightFromGAPD.flight, "FAP");
                savedOffers[flightToken] = 1;
                flightsSessionStore.set(PRICE_DISCREPANCY_LOGGED_OFFER, savedOffers);
            }
        }
    }, [flightToken, flightDetails, searchResults, logPriceDiscrepancyTypes]);
    useEffect(() => {
        const savedOffers = flightsSessionStore.get(PRICE_DISCREPANCY_LOGGED_OFFER) || {};
        if (!savedOffers[flightToken] &&
            flightDetails.fetchStatus === "success" &&
            metaFlightDetails.fetchStatus === "success" &&
            isMetaLandingOfferToken(flightToken)) {
            const flightFromFORAP = metaFlightDetails.flight;
            const flightFromGAPD = flightDetails.flight;
            if (flightFromFORAP && flightFromGAPD) {
                logPriceDiscrepancyTypes(flightFromFORAP, flightFromGAPD, "FORAP");
            }
            savedOffers[flightToken] = 1;
            flightsSessionStore.set(PRICE_DISCREPANCY_LOGGED_OFFER, savedOffers);
        }
    }, [flightDetails, metaFlightDetails, flightToken, logPriceDiscrepancyTypes]);
};
export default useReportPriceDiscrepancy;
